import { useEffect, useState } from 'react';
import { Flex, Box, useTheme, Checkbox } from '@chakra-ui/react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Button, Input } from 'components';
import { rgba } from 'polished';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useElements } from '@stripe/react-stripe-js';
import useBreakpoint from 'hooks/useBreakpoint';
import { PaymentMethodContextProvider, usePaymentMethod } from './PaymentMethodContext';
import {
  Label,
  InformationWrapper,
  ConsentText,
  StyledCardNumberElement,
  StyledCardExpiryElement,
  StyledCardCvcElement,
} from './PaymentMethod.styled';
import { isCorporateThemeSelector } from 'modules/app/appSlice';
import { useSelector } from 'react-redux';
import CurrencyInfo from './CurrencyInfo';

// eslint-disable-next-line no-undef
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string);

interface Props {
  buttonAligment?: 'left' | 'right';
  onSave?: () => void;
  onCancel?: () => void;
  onSkip?: () => void;
}

interface FormData {
  name: string;
  consent: boolean;
}

export const PaymentMethodForm: FC<Props> = ({ buttonAligment = 'left', onSave, onCancel, onSkip }) => {
  const context = usePaymentMethod();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();
  const { isMobile } = useBreakpoint();
  const { t } = useTranslation('paymentMethod');
  const theme = useTheme();
  const { colors } = theme;
  const isCorporateTheme = useSelector(isCorporateThemeSelector);
  const [stripeCompleted, setStripeCompleted] = useState({
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
  });
  const [stripeError, setStripeError] = useState('');
  type Keys = keyof typeof stripeCompleted;
  const elements = useElements();

  const onSubmit = handleSubmit(async data => {
    const errorKey = (Object.keys(stripeCompleted) as Keys[]).find(key => {
      if (!stripeCompleted[key]) {
        return key;
      }
    });
    if (errorKey) {
      setStripeError(t(`stripeErrors.${errorKey}`));
      return;
    }
    await context.createOrUpdatePaymentMethod(data.name, onSave);
  });

  // @ts-ignore
  const handleStripeErrors = (event: StripeElementChangeEvent) => {
    if (event.complete) {
      setStripeCompleted(prev => {
        return { ...prev, [event.elementType]: true };
      });
      setStripeError('');
    }
  };

  const inputStyles = {
    base: {
      fontSize: '16px',
      fontWeight: '500',
      fontSmoothing: 'antialiased',
      color: colors.secondary[800],
      fontFamily: 'body',
      '::placeholder': {
        color: rgba(colors.secondary[800], 0.3),
        fontWeight: '500',
      },
    },
  };

  useEffect(() => {
    elements?.getElement('cardNumber')?.on('change', handleStripeErrors);
    elements?.getElement('cardExpiry')?.on('change', handleStripeErrors);
    elements?.getElement('cardCvc')?.on('change', handleStripeErrors);
  }, [elements]);

  return (
    <Flex
      direction="column"
      as="form"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(e);
        e.stopPropagation();
      }}
    >
      <Label fontSize="lgMin" color="secondary.500" htmlFor="name">
        {t('nameOnCard')}
      </Label>
      <Input
        id="name"
        fontSize="lgMin"
        {...register('name', { required: t('fieldRequired') as string })}
        h={isCorporateTheme ? '50px' : '60px'}
        w={isCorporateTheme ? { base: '100%', md: 'calc(100% - 296px)' } : '100%'}
      />
      <Flex mt="30px" direction={isMobile ? 'column' : 'row'}>
        <Flex direction="column" w="100%" mr={isMobile ? '0' : '32px'}>
          <Label fontSize="lgMin" color="secondary.500" whiteSpace="nowrap">
            {t('cardNumber')}
          </Label>
          <StyledCardNumberElement
            options={{
              style: inputStyles,
              placeholder: '',
            }}
            theme={theme}
            forCorporate={isCorporateTheme}
          />
        </Flex>
        <Flex mt={isMobile ? '30px' : '0'} flexDir={{ base: 'column', sm: 'row' }}>
          <Flex direction="column" w={isMobile ? '100%' : '118px'} mr="30px">
            <Label fontSize="lgMin" color="secondary.500">
              {t('expDate')}
            </Label>
            <StyledCardExpiryElement
              options={{
                style: inputStyles,
                placeholder: '',
              }}
              theme={theme}
              forCorporate={isCorporateTheme}
            />
          </Flex>
          <Flex direction="column" w={isMobile ? '100%' : '118px'} mt={{ base: '20px', sm: 0 }}>
            <Label fontSize="lgMin" color="secondary.500">
              {t('securityCode')}
            </Label>
            <StyledCardCvcElement
              options={{
                style: inputStyles,
                placeholder: '',
              }}
              theme={theme}
              forCorporate={isCorporateTheme}
            />
          </Flex>
        </Flex>
      </Flex>
      {stripeError && (
        <Box as="span" mt="20px" fontWeight="500" color={colors.primary[500]}>
          {stripeError}
        </Box>
      )}
      <InformationWrapper mt="30px" mb="20px" pinkBorder={!!errors.consent} theme={theme} forCorporate={isCorporateTheme}>
        <Flex alignItems="flex-start" p="14px 23px">
          <Checkbox colorScheme="primary" id="consent" mr="15px" {...register('consent', { required: t('fieldRequired') as string })} />
          <ConsentText as="span" fontSize="sm" color="secondary.700">
            {t('chooosePaymentAuthorization')}
          </ConsentText>
        </Flex>
      </InformationWrapper>
      {!onSkip && (
        <Box m="20px 0 30px">
          <CurrencyInfo />
        </Box>
      )}
      {onSkip && (
        <Button variant="unstyled" onClick={onSkip}>
          Skip
        </Button>
      )}
      <Flex flexDir={isMobile ? 'column' : 'row'} w="100%" justifyContent={buttonAligment === 'right' ? 'flex-end' : 'flex-start'}>
        <Button w={isMobile ? '100%' : '165px'} mr="16px" type="submit" isLoading={context.loading} mb={isMobile ? '10px' : '0'}>
          {t('save')}
        </Button>
        {onCancel && !!stripeError && (
          <Button
            w={isMobile ? '100%' : '165px'}
            variant="outline"
            colorScheme="gray"
            color={colors.secondary[800]}
            borderColor={colors.secondary[800]}
            onClick={onCancel}
          >
            {t('cancel')}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

const PaymentMethodEdit: FC<Props> = props => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentMethodContextProvider>
        <PaymentMethodForm {...props} />
      </PaymentMethodContextProvider>
    </Elements>
  );
};

export default PaymentMethodEdit;
